import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Table } from 'antd'
import Divider from 'antd/lib/divider'
import { debounce } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ItemResponseSOVTemplateBeanObject, ListPagination, ListResponseSOVTemplateBean, SOVTemplateBean } from 'src/api/api'
import { withActionsMediaQuery } from 'src/util/Util'
import { StorageKey, storageReaderFactory, storageWriterFactory } from '../../../util/Storage'
import sovTemplateApi from './SOVTemplateApi'
import './style.css'

const Column = Table.Column

const storageWriter = storageWriterFactory('SovTemplate')
const storageReader = storageReaderFactory('SovTemplate')

interface Props {
    areActionsVertical?: boolean
}

const SOVTemplateList: React.FC<Props> = ({ areActionsVertical }) => {

    const [data, setData] = useState<SOVTemplateBean[]>([])
    const [pagination, setPagination] = useState<{
        defaultCurrent: number,
        defaultPageSize: number,
        current: number
    }>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        let searchParams = storageReader.read(StorageKey.SearchParams)
        let page = searchParams.page || pagination.current

        loadData(page)
    }, [])

    const loadData = (page) => {
        sovTemplateApi
            .list(pagination.defaultPageSize, page, searchQuery)
            .then((listResponseSOVTemplateBean: ListResponseSOVTemplateBean) => {

                const array = listResponseSOVTemplateBean.data as Array<ItemResponseSOVTemplateBeanObject>
                const sovTemplateBeans: SOVTemplateBean[] = array.map(item => item.data as SOVTemplateBean)

                if (sovTemplateBeans.length == 0 && page > 1) {
                    loadData(page - 1)
                } else {
                    const newPagination: ListPagination = listResponseSOVTemplateBean.pagination as ListPagination
                    storageWriter.write(StorageKey.SearchParams, {
                        page: newPagination.currentPage
                    })

                    setData(sovTemplateBeans)
                    setPagination(p => {
                        return { ...(p), total: newPagination.total, current: newPagination.currentPage ?? 1 }
                    })
                }
            })
    }

    const remove = (record: SOVTemplateBean) => {
        Modal.confirm({
            title: `Delete ${record.id}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                sovTemplateApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page)
                })
            }
        })
    }

    useEffect(() => {
        if (searchQuery === '' && isFiltered) {
            loadData(1)
            isFiltered = false
        } else {
            debounceSearch()
        }
    }, [searchQuery])

    let isFiltered

    const debounceSearch = debounce(() => {
        loadData(1)
        isFiltered = true
    }, 1000)

    return (
        <div>
            <div style={{ display: 'flex' }}>
                <div className="c-l-search-buttons">
                    <Input
                        addonAfter={<CloseOutlined onClick={() => setSearchQuery('')} />}
                        placeholder="SOV Templates search"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value)
                            debounceSearch()
                        }}
                    />
                </div>
            </div>
            <Table
                rowKey={'id'}
                size={'middle'}
                pagination={{
                    ...pagination,
                    onChange: (page: number) => loadData(page)
                }}
                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id" />
                <Column key="name" title="Name" dataIndex="name" />
                <Column align={'center'} key="action" title="Actions" render={(text: string, record: SOVTemplateBean) => (
                    <span>
                        <Link to={`/sov-template/${record.id}/edit`}>
                            <Button htmlType="button" title="Edit">
                                <EditOutlined />
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Button
                            onClick={() => remove(record)}
                            type="primary"
                            htmlType="button"
                            title="Delete"
                            danger>
                            <DeleteOutlined />
                        </Button>
                    </span>
                )} />
            </Table>
            <Link to={`/sov-template/create`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    )
}

export default withActionsMediaQuery('550px')(SOVTemplateList)
